// Rakhi and Bhai Dooj nudges (M75, D313–D316, docs/indian-touches.md §5):
// on those days, and the day before, the chat home suggests wishing the
// people you call a brother or sister (D143). Everything here runs on the
// device from what it already holds; nothing is sent anywhere (D316).

import greetingsTable from '../../../shared/contracts/greetings.json';
import {
  festivalDayDate,
  findOccasion,
  firstName,
  type FestivalDayId,
  type OccasionId,
} from './greetings';
import { festivalDaysFor, type FestivalId } from './festivalDates';
import { festivalRegion, type FestivalRegion } from './festivalRegion';

/** An occasion that nudges, and optionally one of its days (Bhai Dooj). */
export interface NudgeOccasion {
  occasion: OccasionId;
  day?: FestivalDayId;
}

interface SiblingNudgesFile {
  occasions: NudgeOccasion[];
  relations: string[];
}

// The same list on every platform (D313, D314).
const FILE = (greetingsTable as unknown as { siblingNudges: SiblingNudgesFile })
  .siblingNudges;
export const NUDGE_OCCASIONS: readonly NudgeOccasion[] = FILE.occasions;
const RELATIONS = new Set(FILE.relations);

/** The words of a label, lower-case (D314). */
function labelWords(label: string): string[] {
  return label
    .toLowerCase()
    .split(/[\s.,;:!?'"()[\]{}\-–—/\\|_]+/u)
    .filter(Boolean);
}

/** "didiji" → "didi"; a word that is only "ji" stays. */
function withoutJi(word: string): string {
  for (const ji of ['ji', 'जी']) {
    if (word.length > ji.length && word.endsWith(ji)) {
      return word.slice(0, -ji.length);
    }
  }
  return word;
}

/** Whether a relation name you picked calls them a brother or sister (D314). */
export function isSiblingLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  // "Didi ji" is two words; "Didiji" loses its "ji" ("Paaji" keeps it).
  return labelWords(label).some(
    (word) => RELATIONS.has(word) || RELATIONS.has(withoutJi(word))
  );
}

/**
 * How the card names them: a bare relation word becomes "Asha Didi" (their
 * first name, then the relation); a longer label ("Chhoti Didi") stays.
 */
export function nudgeName(label: string, displayName: string): string {
  const trimmed = label.trim();
  const words = trimmed.split(/\s+/);
  const bare = words.length === 1 && isSiblingLabel(trimmed);
  const first = firstName(displayName);
  return bare && first ? `${first} ${trimmed}` : trimmed;
}

/** Today's nudge: which occasion, its date, and the key it's dismissed by. */
export interface ActiveNudge extends NudgeOccasion {
  /** The festival's (or day's) date in the region, "2026-11-11". */
  date: string;
  /** "raksha-bandhan:2026", "diwali/bhai-dooj:2026" (D316). */
  key: string;
}

const dayNumber = (year: number, month: number, date: number) =>
  Math.round(Date.UTC(year, month - 1, date) / 86_400_000);
const parseDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return dayNumber(y!, m!, d!);
};

/**
 * The nudge for `now` (the device's local date) in the device's region
 * (D266): on the occasion's date or the day before (D313). Null on other
 * days, or where the table has no dates.
 */
export function activeNudge(
  now = new Date(),
  region: FestivalRegion = festivalRegion(now)
): ActiveNudge | null {
  const today = dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate());
  for (const item of NUDGE_OCCASIONS) {
    const occasion = findOccasion(item.occasion);
    if (!occasion) continue;
    const day = item.day
      ? occasion.days?.find((d) => d.id === item.day)
      : undefined;
    if (item.day && !day) continue;
    for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
      const days = festivalDaysFor(region, item.occasion as FestivalId, year);
      if (!days) continue;
      const date = day ? festivalDayDate(day, days) : days.first;
      if (!date) continue;
      const at = parseDay(date);
      if (today === at || today === at - 1) {
        const id = item.day ? `${item.occasion}/${item.day}` : item.occasion;
        return { ...item, date, key: `${id}:${date.slice(0, 4)}` };
      }
    }
  }
  return null;
}

/** Someone the card offers to wish. */
export interface NudgePerson {
  userId: string;
  name: string;
}

/**
 * The people you call a brother or sister in this family, in the family
 * list's order: not you, not people you've blocked, only your own labels
 * (never nicknames or display names, D314).
 */
export function nudgePeople(
  members: ReadonlyArray<{
    userId: string;
    user: { displayName: string };
  }>,
  labels: Readonly<Record<string, string>>,
  currentUserId: string | undefined,
  blockedIds: ReadonlySet<string> = new Set()
): NudgePerson[] {
  return members
    .filter(
      (member) =>
        member.userId !== currentUserId &&
        !blockedIds.has(member.userId) &&
        isSiblingLabel(labels[member.userId])
    )
    .map((member) => ({
      userId: member.userId,
      name: nudgeName(labels[member.userId]!, member.user.displayName),
    }));
}

/**
 * Where tapping a person goes (D315): their direct chat, with the greeting
 * sheet on this occasion and day, as query parameters.
 */
export function nudgeGreetParams(nudge: NudgeOccasion): Record<string, string> {
  return nudge.day
    ? { greet: nudge.occasion, greetDay: nudge.day }
    : { greet: nudge.occasion };
}

// ---- Dismissed, per festival and year, on this device (D316) ----

const DISMISSED_KEY = 'twinden:siblingNudgesDismissed';

export function dismissedNudges(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter((k): k is string => typeof k === 'string')
      : [];
  } catch {
    return [];
  }
}

export function isNudgeDismissed(key: string): boolean {
  return dismissedNudges().includes(key);
}

/** Keeps this year's and last year's keys; older ones go. */
export function dismissNudge(key: string, now = new Date()): void {
  const year = now.getFullYear();
  const keep = dismissedNudges().filter((k) => {
    const y = Number(k.slice(k.lastIndexOf(':') + 1));
    return y >= year - 1;
  });
  if (!keep.includes(key)) keep.push(key);
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(keep));
  } catch {
    // Private mode: it goes for this visit only.
  }
}
